(() => {
  if (window.CAFThemeHotfixShared) return;

  const PDP_LAYOUT_STORAGE_KEY = 'caf_pdp_layout_mode';
  const DEFAULT_PDP_LAYOUT_MODE = '3';
  const PRODUCT_DETAILS_SELECTOR = '.product__details--product-page';
  const JDGM_PREVIEW_BADGE_SELECTOR = '.jdgm-preview-badge[data-id]';
  const JUDGE_ME_REVIEW_WIDGET_SELECTORS = [
    '#judgeme_product_reviews',
    '#judgeme_all_reviews_page',
    '.jdgm-all-reviews-page',
    '.jdgm-all-reviews-widget',
    '.jdgm-all-reviews-widget-v2025',
    '.jdgm-widget-revamp',
  ];
  const JUDGE_ME_REVIEW_WIDGET_SELECTOR = JUDGE_ME_REVIEW_WIDGET_SELECTORS.join(', ');
  const JUDGE_ME_REVIEW_IMAGE_SELECTOR = JUDGE_ME_REVIEW_WIDGET_SELECTORS.map((selector) => `${selector} img`).join(', ');
  const JUDGE_ME_REVIEW_IMAGE_SHELL_SELECTOR = [
    '.jdgm-rev__pic-link',
    '.jdgm-rev__pic',
    '.jdgm-rev__picture-link',
    '.jdgm-gallery__image-wrapper',
    '.jdgm-review-card__image',
    '.jdgm-review-card__media-item',
    '.jdgm-media-fieldset__medium',
  ].join(', ');
  const JUDGE_ME_REVIEW_IMAGE_GROUP_SELECTOR = [
    '.jdgm-rev__pics',
    '.jdgm-review-card__media',
    '.jdgm-review-card__pictures',
    '.jdgm-gallery',
    '.jdgm-medias',
  ].join(', ');
  const TEEINBLUE_EVENT_COMPONENT_INJECTED = 'teeinblue-event-component-injected';
  const JUDGE_ME_LOAD_EVENTS = ['jdgm:loaded', 'judgeme:loaded'];
  const POPUP_MODAL_SURFACE_SELECTOR = '.vm--modal, .vm--container, .tee-dialog';
  const DISCOUNT_PRICE_APP_SELECTOR = '.product-block--discount-price-app';
  const CART_PREVIEW_CHECKOUT_SELECTOR = '.tee-preview-button__checkout';
  const CART_PREVIEW_MUTATION_SELECTOR = `${CART_PREVIEW_CHECKOUT_SELECTOR}, ${POPUP_MODAL_SURFACE_SELECTOR}`;
  const MOBILE_POPUP_BREAKPOINT = 768;

  const hasBodyClassMatch = (pattern) => pattern.test(document.body?.className || '');
  const pathIncludes = (segment) => window.location.pathname.includes(segment);
  const isPathCart = () => window.location.pathname === '/cart' || window.location.pathname.startsWith('/cart');
  const isProductContext = (fallbackSelector) => {
    if (hasBodyClassMatch(/template-product|product/)) return true;
    if (pathIncludes('/products/')) return true;
    return fallbackSelector ? Boolean(document.querySelector(fallbackSelector)) : false;
  };
  const hasFacetedFilterUi = () => {
    return Boolean(document.querySelector('[data-faceted-filter-form], .faceted-filters, .faceted-filter-group'));
  };
  const isCollectionOrSearchContext = () => {
    return hasBodyClassMatch(/template-(collection|search)/) || pathIncludes('/collections/') || pathIncludes('/search');
  };
  const isCartContext = () => {
    return hasBodyClassMatch(/\btemplate-cart\b|\bcart\b/) || isPathCart();
  };
  const runWithDelays = (fn, delays) => {
    delays.forEach((delay) => {
      window.setTimeout(fn, delay);
    });
  };
  const createRafThrottled = (fn, guard = () => false) => {
    let pending = false;

    return (...args) => {
      if (pending || guard()) return;
      pending = true;
      window.requestAnimationFrame(() => {
        pending = false;
        if (guard()) return;
        fn(...args);
      });
    };
  };
  const nodeMatchesOrContains = (node, selector) => {
    if (!(node instanceof Element)) return false;
    if (node.matches(selector)) return true;
    return Boolean(node.querySelector(selector));
  };
  const mutationsContainAddedNode = (mutations, selector) => {
    return mutations.some((mutation) => {
      return Array.from(mutation.addedNodes || []).some((node) => nodeMatchesOrContains(node, selector));
    });
  };
  const mutationsHaveAddedNodes = (mutations) => {
    return mutations.some((mutation) => (mutation.addedNodes?.length || 0) > 0);
  };
  const bindJudgeMeAndTeeinblueEvents = (handler) => {
    document.addEventListener(TEEINBLUE_EVENT_COMPONENT_INJECTED, handler);
    JUDGE_ME_LOAD_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, handler);
    });
  };
  const clearInlineContextMenuLocks = () => {
    [document, document.documentElement, document.body].forEach((node) => {
      if (!node) return;
      if (typeof node.oncontextmenu === 'function') {
        node.oncontextmenu = null;
      }
    });

    document.querySelectorAll('[oncontextmenu]').forEach((node) => {
      node.removeAttribute('oncontextmenu');
      if (typeof node.oncontextmenu === 'function') {
        node.oncontextmenu = null;
      }
    });
  };
  const allowNativeContextMenu = () => {
    if (window.__cafNativeContextMenuAllowed) return;
    window.__cafNativeContextMenuAllowed = true;

    const clearLocks = () => clearInlineContextMenuLocks();

    window.addEventListener(
      'contextmenu',
      (event) => {
        clearLocks();
        event.stopImmediatePropagation();
      },
      true,
    );

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', clearLocks, { once: true });
    } else {
      clearLocks();
    }

    runWithDelays(clearLocks, [120, 600, 1500, 3600]);
  };
  const removeClassFromAll = (scope, selector, className) => {
    scope.querySelectorAll(selector).forEach((node) => {
      node.classList.remove(className);
    });
  };
  const removeAll = (scope, selector) => {
    scope.querySelectorAll(selector).forEach((node) => {
      node.remove();
    });
  };
  const unwrapAll = (scope, selector) => {
    scope.querySelectorAll(selector).forEach((wrapper) => {
      if (wrapper.parentElement) {
        while (wrapper.firstChild) {
          wrapper.parentElement.insertBefore(wrapper.firstChild, wrapper);
        }
      }
      wrapper.remove();
    });
  };
  const setImportantStyles = (node, styles) => {
    if (!(node instanceof HTMLElement)) return;
    Object.entries(styles).forEach(([property, value]) => {
      node.style.setProperty(property, value, 'important');
    });
  };
  const isJudgeMeReviewContext = () => {
    return (
      pathIncludes('/pages/reviews') ||
      isProductContext('#judgeme_product_reviews') ||
      Boolean(document.querySelector(JUDGE_ME_REVIEW_WIDGET_SELECTOR))
    );
  };
  const hasHealthyJudgeMeReviewImage = (image) => {
    if (!(image instanceof HTMLImageElement)) return false;
    if (image.dataset.cafJdgmBrokenImage === 'true') return false;
    return !image.complete || image.naturalWidth > 0;
  };
  const refreshJudgeMeReviewImageGroup = (group) => {
    if (!(group instanceof Element)) return;
    const hasHealthyImage = Array.from(group.querySelectorAll('img')).some(hasHealthyJudgeMeReviewImage);
    group.classList.toggle('caf-jdgm-review-media-empty', !hasHealthyImage);
  };
  const markBrokenJudgeMeReviewImage = (image) => {
    if (!(image instanceof HTMLImageElement)) return;
    if (!image.closest(JUDGE_ME_REVIEW_WIDGET_SELECTOR)) return;

    image.dataset.cafJdgmBrokenImage = 'true';
    image.classList.add('caf-jdgm-broken-review-image');

    const shell = image.closest(JUDGE_ME_REVIEW_IMAGE_SHELL_SELECTOR);
    if (shell) {
      shell.classList.add('caf-jdgm-broken-review-media');
    }

    refreshJudgeMeReviewImageGroup(image.closest(JUDGE_ME_REVIEW_IMAGE_GROUP_SELECTOR));
  };
  const markExistingBrokenJudgeMeReviewImages = (scope = document) => {
    if (!isJudgeMeReviewContext()) return;

    scope.querySelectorAll(JUDGE_ME_REVIEW_IMAGE_SELECTOR).forEach((image) => {
      if (image.complete && image.naturalWidth === 0) {
        markBrokenJudgeMeReviewImage(image);
      } else {
        refreshJudgeMeReviewImageGroup(image.closest(JUDGE_ME_REVIEW_IMAGE_GROUP_SELECTOR));
      }
    });
  };
  const guardBrokenJudgeMeReviewImages = () => {
    if (window.CAFJudgeMeBrokenReviewImageGuard) return;
    window.CAFJudgeMeBrokenReviewImageGuard = true;

    const scheduleScan = createRafThrottled(() => markExistingBrokenJudgeMeReviewImages());

    document.addEventListener(
      'error',
      (event) => {
        markBrokenJudgeMeReviewImage(event.target);
      },
      true,
    );

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleScan, { once: true });
    } else {
      scheduleScan();
    }

    bindJudgeMeAndTeeinblueEvents(scheduleScan);
    runWithDelays(scheduleScan, [300, 1200, 3000, 6000]);

    new MutationObserver((mutations) => {
      if (mutationsContainAddedNode(mutations, JUDGE_ME_REVIEW_IMAGE_SELECTOR)) {
        scheduleScan();
      }
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  const normalizePdpLayoutMode = (value) => {
    const raw = String(value || '')
      .trim()
      .toLowerCase();

    const aliases = {
      1: '1',
      2: '2',
      3: '3',
      compact: '1',
      'compact-safe': '1',
      safe: '1',
      conversion: '2',
      'conversion-first': '2',
      urgency: '2',
      ultra: '3',
      'ultra-compact': '3',
      'ultra-compact-mobile': '3',
      mobile: '3',
    };

    return aliases[raw] || DEFAULT_PDP_LAYOUT_MODE;
  };

  const resolvePdpLayoutMode = () => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('pdp_mode') || params.get('pdp_layout');

    if (fromQuery) {
      const normalized = normalizePdpLayoutMode(fromQuery);
      try {
        window.localStorage.setItem(PDP_LAYOUT_STORAGE_KEY, normalized);
      } catch (error) {
        // Ignore storage failures in private browsing modes.
      }
      return normalized;
    }

    try {
      const fromStorage = window.localStorage.getItem(PDP_LAYOUT_STORAGE_KEY);
      if (fromStorage) {
        return normalizePdpLayoutMode(fromStorage);
      }
    } catch (error) {
      // Ignore storage failures and fall back to default.
    }

    return DEFAULT_PDP_LAYOUT_MODE;
  };

  const normalizeInlineText = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim();

  const extractFirstInteger = (value) => {
    const match = String(value || '').match(/(\d[\d,.]*)/);
    if (!match) return null;
    const numeric = Number(match[1].replace(/[^\d]/g, ''));
    if (!Number.isFinite(numeric)) return null;
    return Math.max(0, Math.round(numeric));
  };

  const formatIntegerWithCommas = (value) => Number(value || 0).toLocaleString('en-US');
  const parseFirstDecimal = (value) => {
    const match = String(value || '').match(/(\d+(?:\.\d+)?)/);
    if (!match) return null;
    const numeric = Number.parseFloat(match[1]);
    return Number.isFinite(numeric) ? numeric : null;
  };
  const CAF_REVIEW_STAR_SVG =
    '<svg class="caf-icon caf-icon--rating-star" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3l2.5 5.5 6 .5-4.5 4 1.5 6L12 16l-5.5 3 1.5-6-4.5-4 6-.5L12 3z" fill="currentColor"/></svg>';

  /* Review numbers: only what Judge.me reports (owner decision 2026-10-12, DECISIONS "Real review numbers").
     - a product with reviews of its own shows its own average and count;
     - a product without any shows the store-wide rating and review count in the same row, or nothing
       (theme setting "Products without reviews"); aria-label/title identify the store-wide source.
     Nothing here may invent, pad or round up a rating or a count. */

  // Store-wide rating: printed by layout/theme.liquid from the shop metafields Judge.me keeps up to date.
  const getStoreReviewFacts = () => {
    const source = window.CAFStoreRating;
    if (!source) return null;

    const rating = Number(source.rating);
    const count = Math.round(Number(source.count));
    if (!(rating > 0 && rating <= 5) || !(count > 0)) return null;

    return { count, rating: rating.toFixed(2), showOnUnreviewed: source.unreviewed !== 'none' };
  };

  // One product's own numbers, read from the attributes Judge.me prints on its badge. No reviews = { count: 0, rating: null }.
  const readJudgeMeBadgeFacts = (widget) => {
    const badge = widget?.querySelector?.('.jdgm-prev-badge');
    const count = extractFirstInteger(badge?.getAttribute('data-number-of-reviews')) || 0;
    const average = parseFirstDecimal(badge?.getAttribute('data-average-rating')) || 0;
    if (count < 1 || average <= 0) return { count: 0, rating: null };

    return { count, rating: Math.min(5, average).toFixed(1) };
  };

  const formatOwnReviewBadge = (rating, count) =>
    `<span class="caf-jdgm-inline-rating">${rating}</span> <span class="caf-jdgm-inline-star" aria-hidden="true">${CAF_REVIEW_STAR_SVG}</span> <span class="caf-jdgm-inline-count">(${formatIntegerWithCommas(count)})</span>`;
  const formatStoreReviewBadge = (rating, count) =>
    `<span class="caf-jdgm-inline-rating">${rating}</span> <span class="caf-jdgm-inline-star" aria-hidden="true">${CAF_REVIEW_STAR_SVG}</span> <span class="caf-jdgm-inline-count caf-jdgm-inline-count--store">(${formatIntegerWithCommas(count)})</span>`;

  // Judge.me prints the badge from a product metafield; a product it has never synced has an empty widget.
  const ensureJudgeMePreviewBadgeText = (widget) => {
    let previewBadge = widget.querySelector('.jdgm-prev-badge');
    if (!previewBadge) {
      previewBadge = document.createElement('div');
      previewBadge.className = 'jdgm-prev-badge';
      widget.appendChild(previewBadge);
    }

    if (!previewBadge.querySelector('.jdgm-prev-badge__text')) {
      const text = document.createElement('span');
      text.className = 'jdgm-prev-badge__text';
      previewBadge.appendChild(text);
    }

    return previewBadge;
  };

  const setAttributeIfChanged = (node, name, value) => {
    if (value === null) {
      if (node.hasAttribute(name)) node.removeAttribute(name);
      return;
    }
    if (node.getAttribute(name) !== value) node.setAttribute(name, value);
  };

  // Runs on every Judge.me / TeeInBlue redraw, so each write happens only when the value differs: a write that
  // repeats itself re-triggers the observers that call this (the 60-times-a-second loop found on 2026-10-12).
  const applyJudgeMeRealRatings = (scope = document) => {
    if (!scope || typeof scope.querySelectorAll !== 'function') return;

    const store = getStoreReviewFacts();

    scope.querySelectorAll(JDGM_PREVIEW_BADGE_SELECTOR).forEach((widget) => {
      if (!normalizeInlineText(widget.getAttribute('data-id'))) return;

      const previewBadge = ensureJudgeMePreviewBadgeText(widget);
      const own = readJudgeMeBadgeFacts(widget);
      const mode = own.rating ? 'own' : store?.showOnUnreviewed ? 'store' : 'none';
      const rating = mode === 'own' ? own.rating : mode === 'store' ? store.rating : null;

      let html = '';
      let label = null;
      let hint = null;
      if (mode === 'own') {
        html = formatOwnReviewBadge(rating, own.count);
        label = `${rating} out of 5 stars, ${formatIntegerWithCommas(own.count)} ${own.count === 1 ? 'review' : 'reviews'}`;
      } else if (mode === 'store') {
        html = formatStoreReviewBadge(rating, store.count);
        label = `Store rating ${rating} out of 5, from ${formatIntegerWithCommas(store.count)} reviews of all products`;
        hint = `Store rating: the average of ${formatIntegerWithCommas(store.count)} reviews of all our products`;
      }

      const renderKey = `${mode}|${rating}|${mode === 'store' ? store.count : own.count}`;
      widget.querySelectorAll('.jdgm-prev-badge__text').forEach((node) => {
        // Judge.me re-renders its own text into this node on some loads: write again only when ours is gone.
        const oursIsGone = Boolean(html) && !node.querySelector('.caf-jdgm-inline-rating');
        if (node.dataset.cafRendered !== renderKey || oursIsGone) {
          node.innerHTML = html;
          node.dataset.cafRendered = renderKey;
        }
        setAttributeIfChanged(node, 'aria-label', label);
        setAttributeIfChanged(node, 'title', hint);
      });

      setAttributeIfChanged(widget, 'data-caf-review-source', mode);
      // Stars for the store rating, rounded to half a star like Judge.me's own rows (4.97 → "5"); CSS draws them.
      setAttributeIfChanged(widget, 'data-caf-store-stars', mode === 'store' ? String(Math.round(Number(rating) * 2) / 2) : null);
      // 'none' leaves the badge without the theme's display hooks, so Judge.me's empty "No reviews" row stays hidden.
      setAttributeIfChanged(previewBadge, 'data-caf-preview-review-format', mode === 'none' ? null : 'rating-count');
      setAttributeIfChanged(previewBadge, 'data-caf-display-rating', rating);
    });
  };

  allowNativeContextMenu();
  guardBrokenJudgeMeReviewImages();

  window.CAFThemeHotfixShared = {
    CART_PREVIEW_CHECKOUT_SELECTOR,
    CART_PREVIEW_MUTATION_SELECTOR,
    DEFAULT_PDP_LAYOUT_MODE,
    DISCOUNT_PRICE_APP_SELECTOR,
    JDGM_PREVIEW_BADGE_SELECTOR,
    JUDGE_ME_REVIEW_WIDGET_SELECTORS,
    JUDGE_ME_REVIEW_IMAGE_GROUP_SELECTOR,
    JUDGE_ME_REVIEW_IMAGE_SELECTOR,
    JUDGE_ME_REVIEW_IMAGE_SHELL_SELECTOR,
    JUDGE_ME_REVIEW_WIDGET_SELECTOR,
    JUDGE_ME_LOAD_EVENTS,
    MOBILE_POPUP_BREAKPOINT,
    PDP_LAYOUT_STORAGE_KEY,
    POPUP_MODAL_SURFACE_SELECTOR,
    PRODUCT_DETAILS_SELECTOR,
    TEEINBLUE_EVENT_COMPONENT_INJECTED,
    applyJudgeMeRealRatings,
    bindJudgeMeAndTeeinblueEvents,
    clearInlineContextMenuLocks,
    createRafThrottled,
    extractFirstInteger,
    getStoreReviewFacts,
    hasBodyClassMatch,
    hasFacetedFilterUi,
    isCartContext,
    isCollectionOrSearchContext,
    isPathCart,
    isProductContext,
    mutationsContainAddedNode,
    mutationsHaveAddedNodes,
    nodeMatchesOrContains,
    normalizeInlineText,
    normalizePdpLayoutMode,
    pathIncludes,
    readJudgeMeBadgeFacts,
    removeAll,
    removeClassFromAll,
    resolvePdpLayoutMode,
    runWithDelays,
    setImportantStyles,
    unwrapAll,
  };
})();
